<template>
  <GenericCard
    has-documents
    :document-types="documentTypes"
    :document-identifier="documentIdentifier"
    :locked="locked"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    title="Party Details"
    :create="create"
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col>
          <v-text-field
            v-model="section.first_name"
            autocomplete="off"
            label="First Name*"
            outlined
            :error-messages="getErrorMessages(v$.section.first_name)"
            @input="v$.section.first_name.$touch()"
            @blur="v$.section.first_name.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.middle_name"
            autocomplete="off"
            label="Middle Name"
            outlined
            :error-messages="getErrorMessages(v$.section.middle_name)"
            @input="v$.section.middle_name.$touch()"
            @blur="v$.section.middle_name.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.last_name"
            autocomplete="off"
            label="Last Name*"
            outlined
            :error-messages="getErrorMessages(v$.section.last_name)"
            @input="v$.section.last_name.$touch()"
            @blur="v$.section.last_name.$touch()"
          />
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col
          class="pl-4"
        >
          <BirthDate
            :date="section.dob"
            :uncertain="section.uncertain_dob"
            required
            outlined
            :disabled="locked"
            for-party-edit
            @update-box="onUncertainDateUpdate"
            @update-date="onDoBUpdate"
            @tab-out="v$.section.dob.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.city_of_birth"
            autocomplete="off"
            label="City of Birth"
            outlined
            :error-messages="getErrorMessages(v$.section.city_of_birth)"
            @input="v$.section.city_of_birth.$touch()"
            @blur="v$.section.city_of_birth.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.state_of_birth"
            autocomplete="off"
            label="State/Province of Birth"
            outlined
            :error-messages="getErrorMessages(v$.section.state_of_birth)"
            @input="v$.section.state_of_birth.$touch()"
            @blur="v$.section.state_of_birth.$touch()"
          />
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="section.birth_country_id"
            :items="optionsCountries"
            item-text="name"
            item-value="id"
            label="Country of birth"
            outlined
            :error-messages="getErrorMessages(v$.section.birth_country_id)"
            @input="v$.section.birth_country_id.$touch()"
            @blur="v$.section.birth_country_id.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="4"
        >
          <v-autocomplete
            v-model="section.nationality_ids"
            :items="optionsCountries"
            item-text="name"
            item-value="id"
            label="Nationalities"
            outlined
            multiple
            :error-messages="getErrorMessages(v$.section.nationality_ids)"
            @input="v$.section.nationality_ids.$touch()"
            @blur="v$.section.nationality_ids.$touch()"
          />
        </v-col>
        <v-col
          cols="4"
        >
          <v-autocomplete
            v-model="section.gender_id"
            :items="optionsGenders"
            item-text="name"
            item-value="id"
            label="Gender*"
            outlined
            :error-messages="getErrorMessages(v$.section.gender_id)"
            @input="v$.section.gender_id.$touch()"
            @blur="v$.section.gender_id.$touch()"
          />
        </v-col>
        <v-col
          cols="auto"
        >
          <label for="deceased">Deceased?</label>
          <v-radio-group
            id="deceased"
            v-model="section.deceased"
            row
            :error-messages="getErrorMessages(v$.section.deceased)"
            class="mt-0"
            @input="v$.section.deceased.$touch()"
            @blur="v$.section.deceased.$touch()"
          >
            <v-radio
              label="No"
              :value="false"
            />
            <v-radio
              label="Yes"
              :value="true"
            />
          </v-radio-group>
        </v-col>
      </v-row>
    </v-form>

    <PEPartyAdversitiesDialog
      v-model="showAdversitiesDialog"
      :adversities="adversities"
      :on-proceed="onAdversityResolved"
    />

    <PEPartyDuplicatesDialog
      v-model="showDuplicatesDialog"
      :duplicates="duplicates"
      :on-proceed="onDuplicateResolved"
    />
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { helpers, required, requiredUnless } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import BirthDate from '@/components/Fields/BirthDate.vue'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard.vue'
import PEPartyAdversitiesDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyAdversitiesDialog.vue'
import PEPartyDuplicatesDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyDuplicatesDialog.vue'

export default {
  name: 'PartyDetailsCard',

  components: {
    BirthDate,
    GenericCard,
    PEPartyAdversitiesDialog,
    PEPartyDuplicatesDialog,
  },

  props: {
    caseId: {
      type: String,
      default: null,
    },

    create: {
      type: Boolean,
      default: false,
    },

    item: {
      type: Object,
      required: true,
    },

    recordId: {
      type: String,
      default: null,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      adversities: [],
      documentIdentifier: 'details-party-details',

      documentTypes: [
        { text: 'Birth Certificate', value: 'birth-certificate' },
        { text: 'Death Certificate', value: 'death-certificate' },
        { text: 'National or State ID', value: 'national-or-state-id' },
        { text: 'Name Change Document', value: 'name-change-document' },
        { text: 'Immigration Status Document', value: 'immigration-status-document' },
        { text: 'Other', value: 'other' },
      ],

      duplicates: [],
      section: {},
      showAdversitiesDialog: false,
      showDuplicatesDialog: false,
    }
  },

  validations () {
    return {
      section: {
        birth_country_id: {},
        city_of_birth: {},
        deceased: {},
        dob: { required: requiredUnless(this.section.uncertain_dob) },
        first_name: { required: helpers.withMessage('First Name is Required', required) },
        gender_id: { required: helpers.withMessage('Gender is Required', required) },
        last_name: { required: helpers.withMessage('Last Name is Required', required) },
        middle_name: {},
        nationality_ids: {},
        state_of_birth: {},
        uncertain_dob: { required: requiredUnless(this.section.dob) },
      },
    }
  },

  computed: {
    cardIdentifier () { return 'party-details-card' },

    locked: {
      get () {
        if (this.create) return false
        return this.pePersonalDetailsCardLock
      },

      set (value) {
        this.setPePersonalDetailsCardLock(value)
      },
    },

    optionsCountries () { return this.getCachedDropdown('countries') },
    optionsGenders () { return this.getCachedDropdown('genders') },

    ...mapGetters(PARTY_NAMESPACE, [
      'peParty',
      'pePersonalDetailsCardLock',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (this.create) {
      this.peSetParty({
        adversity_resolution_comment: null,
        birth_country_id: null,
        city_of_birth: '',
        deceased: null,
        dob: '',
        first_name: '',
        gender_id: '',
        last_name: '',
        middle_name: '',
        nationality_ids: [],
        state_of_birth: '',
        uncertain_dob: false,
      })
    }

    this.initPePersonalDetailsCard()
    this.getDropdownOptions({ options: false, path: 'countries' })
    this.getDropdownOptions({ options: false, path: 'genders' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onAdversityResolved (forceCreationComment) {
      this.showAdversitiesDialog = false
      this.section.adversity_resolution_comment = forceCreationComment

      this.peCreateParty({
        legalCaseId: this.caseId,
        party: this.section,
        recordId: this.recordId,
      }).
        then(_ => {
          this.displayAlert('success', 'Party created successfully')
          this.$router.push({
            name: 'partyEdit',
            params: { id: String(this.section.id) },
          })
        })
    },

    async onCardCreate () {
      this.duplicates = await this.detectDuplicate({
        first_name: this.section.first_name,
        last_name: this.section.last_name,
      })

      if (this.duplicates.length > 0) this.showDuplicatesDialog = true
      else this.onDuplicateResolved()
    },

    async onCardRefresh () {
      return this.pePersonalDetailsCardRefresh()
    },

    async onCardUpdate () {
      return this.pePersonalDetailsCardUpdate({ id: this.peParty.id, section: this.section })
    },

    onDoBUpdate (date) {
      this.v$.section.dob.$touch()
      this.v$.section.uncertain_dob.$touch()
      this.section.dob = date
    },

    async onDuplicateResolved () {
      this.showDuplicatesDialog = false

      this.adversities = await this.detectAdversity({
        first_name: this.section.first_name,
        last_name: this.section.last_name,
      })

      if (this.adversities.length > 0) this.showAdversitiesDialog = true
      else this.onAdversityResolved()
    },

    onSectionUnlock () {
      this.setPePersonalDetailsCardLock(false)
    },

    onUncertainDateUpdate (value) {
      this.v$.section.dob.$touch()
      this.v$.section.uncertain_dob.$touch()
      this.section.uncertain_dob = value
    },

    async submitForm () {
      if (this.create) {
        this.onCardCreate()
        // Returning `false` breaks the default chain of actions.
        // See app/javascript/packs/components/Party/PartyEdit/SectionCards/GenericCard.vue#onCardSubmit for more
        // We want to break it as in the current card, we want to run specific
        // verifications first.
        return false
      }
      if (this.locked) { return this.onSectionUnlock() }

      return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peSetParty',
      'setPePersonalDetailsCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'detectAdversity',
      'detectDuplicate',
      'initPePersonalDetailsCard',
      'peCreateParty',
      'pePersonalDetailsCardRefresh',
      'pePersonalDetailsCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
